import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {UsersService} from '../../../../services/users/users.service';
import {TasksService} from '../../../../services/tasks/tasks.service';
import {GoalsService} from '../../../../services/goals/goals.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../../brokeragency/member-addedit/member-addedit.component';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-superadmin-rewards-claimed',
    templateUrl: './superadmin-rewards-claimed.component.html',
    styleUrls: ['./superadmin-rewards-claimed.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminRewardsClaimedComponent implements OnInit, OnDestroy {
  public columnsToDisplay: string[] = ['createdAt', 'status', 'user', 'taskGoal', 'actions'];
  public items: any[] = [];
  public searchValue = '';
  public page = 1;
  public limit = 30;
  public endOfList = false;
  public loading = false;
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;
  private readonly statusCycle = ['presented', 'claimed', 'active'];
  private readonly addEditFields: Array<Field> = [
    {name: 'rewardId', label: 'Reward ID', fieldType: FieldType.F_STRING, required: true},
    {name: 'createdAt', label: 'Created At', fieldType: FieldType.F_STRING, required: true},
    {name: 'status', label: 'Status', fieldType: FieldType.F_STRING, required: true},
    {name: 'userId', label: 'User ID', fieldType: FieldType.F_STRING, required: true},
    {name: 'goalId', label: 'Goal ID', fieldType: FieldType.F_STRING, required: false},
    {name: 'taskId', label: 'Task ID', fieldType: FieldType.F_STRING, required: false},
    {name: 'parameters', label: 'Parameters', fieldType: FieldType.F_JSON, required: false},
    {name: 'formData', label: 'Form Data', fieldType: FieldType.F_JSON, required: false},
  ];

  public userCache: Record<string, string> = {};
  public goalTaskCache: Record<string, string> = {};

  constructor(
    private rewardsAdminService: RewardsAdminService,
    private usersService: UsersService,
    private tasksService: TasksService,
    private goalsService: GoalsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadItems(true);
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 1;
      this.endOfList = false;
      this.items = [];
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const claimed = await this.rewardsAdminService.getClaimedRewards(this.searchValue, '', this.page, this.limit);
      if (!claimed || claimed.length === 0) {
        this.endOfList = true;
      } else {
        this.items = this.items.concat(claimed);
        this.page += 1;
        claimed.forEach((reward) => this.populateCaches(reward));
      }
    } catch (error) {
      console.error('Error loading claimed rewards', error);
    } finally {
      this.loading = false;
    }
  }

  async populateCaches(reward: any): Promise<void> {
    if (reward.taskId && !this.goalTaskCache[reward.taskId]) {
      try {
        const task = await this.tasksService.getTaskById(reward.userId, reward.taskId);
        this.goalTaskCache[reward.taskId] = task.title || task.description || task.specific || '';
      } catch {
        this.goalTaskCache[reward.taskId] = '';
      }
    }
    if (reward.goalId && !this.goalTaskCache[reward.goalId]) {
      try {
        const goal = await this.goalsService.getGoalById(reward.goalId);
        this.goalTaskCache[reward.goalId] = `${goal.title} $${goal.cost}`;
      } catch {
        this.goalTaskCache[reward.goalId] = '';
      }
    }
    if (!this.userCache[reward.userId]) {
      try {
        const user = await this.usersService.getUserById(reward.userId);
        this.userCache[reward.userId] = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      } catch {
        this.userCache[reward.userId] = 'Old User';
      }
    }
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  addClaimed(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.getDialogFields(), 'Claimed Reward', true, 'Add'),
    });
    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (!dialogResult) {
        return;
      }

      const payload = this.buildClaimedPayload(dialogResult);
      if (!payload) {
        return;
      }
      this.rewardsAdminService.postClaimedReward(payload).then(() => {
        this.loadItems(true);
        this.snackBar.open('Claimed reward successfully created', 'Close', {duration: 2000});
      }).catch((error) => {
        console.error('Error creating claimed reward', error);
        this.snackBar.open('Unable to create claimed reward', 'Close', {duration: 3000});
      });
    });
  }

  async editClaimed(item: any): Promise<void> {
    const claimedId = item?.id;
    if (!claimedId) {
      return;
    }

    try {
      const fullClaimedReward = await this.rewardsAdminService.getClaimedRewardById(claimedId);
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel(
          this.toDialogClaimedData(fullClaimedReward),
          this.getDialogFields(),
          'Claimed Reward',
        ),
      });
      dialogRef.afterClosed().subscribe((dialogResult) => {
        if (!dialogResult) {
          return;
        }

        const payload = this.buildClaimedPayload(dialogResult, fullClaimedReward);
        if (!payload) {
          return;
        }
        this.rewardsAdminService.putClaimedReward(payload).then(() => {
          this.loadItems(true);
          this.snackBar.open('Claimed reward successfully updated', 'Close', {duration: 2000});
        }).catch((error) => {
          console.error('Error updating claimed reward', error);
          this.snackBar.open('Unable to update claimed reward', 'Close', {duration: 3000});
        });
      });
    } catch (error) {
      console.error('Error loading claimed reward', error);
      this.snackBar.open('Unable to load claimed reward details', 'Close', {duration: 3000});
    }
  }

  async cycleStatus(event: Event, item: any): Promise<void> {
    event.stopPropagation();
    const currentStatus = (item?.status || '').toLowerCase();
    const nextStatus = this.statusCycle[(this.statusCycle.indexOf(currentStatus) + 1) % this.statusCycle.length];
    const payload = { ...item, status: nextStatus };
    try {
      await this.rewardsAdminService.putClaimedReward(payload);
      item.status = nextStatus;
      this.snackBar.open(`Claimed reward status updated to ${nextStatus}`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error cycling claimed reward status', error);
      this.snackBar.open('Unable to update claimed reward status', 'Close', { duration: 3000 });
    }
  }

  async deleteClaimed(event: Event, item: any): Promise<void> {
    event.stopPropagation();
    const claimedId = item?.id;
    if (!claimedId) {
      return;
    }

    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this claimed reward?');
    if (!confirm) {
      return;
    }

    try {
      await this.rewardsAdminService.deleteClaimedReward({id: claimedId});
      this.loadItems(true);
      this.snackBar.open('Claimed reward successfully deleted', 'Close', {duration: 2000});
    } catch (error) {
      console.error('Error deleting claimed reward', error);
      this.snackBar.open('Unable to delete claimed reward', 'Close', {duration: 3000});
    }
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadItems();
    }
  }

  private getDialogFields(): Array<Field> {
    return this.addEditFields.map((field) => ({...field}));
  }

  private toDialogClaimedData(item: any): Record<string, any> {
    return {
      id: item?.id || '',
      rewardId: item?.rewardId || '',
      createdAt: item?.createdAt || '',
      status: item?.status || '',
      userId: item?.userId || '',
      goalId: item?.goalId || '',
      taskId: item?.taskId || '',
      parameters: this.parseJsonValue(item?.parameters),
      formData: this.parseJsonValue(item?.formData),
    };
  }

  private buildClaimedPayload(dialogResult: Record<string, any>, currentItem?: any): Record<string, any> | null {
    const rewardId = String(dialogResult?.rewardId || '').trim();
    const createdAt = String(dialogResult?.createdAt || '').trim();
    const status = String(dialogResult?.status || '').trim();
    const userId = String(dialogResult?.userId || '').trim();

    if (!rewardId || !createdAt || !status || !userId) {
      this.snackBar.open('Reward ID, created at, status, and user ID are required', 'Close', {duration: 3000});
      return null;
    }

    const payload: Record<string, any> = {
      rewardId,
      createdAt,
      status,
      userId,
      goalId: dialogResult?.goalId || '',
      taskId: dialogResult?.taskId || '',
      parameters: this.parseJsonValue(dialogResult?.parameters),
      formData: this.parseJsonValue(dialogResult?.formData),
    };

    if (currentItem?.id) {
      payload.id = currentItem.id;
    }

    return payload;
  }

  private parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }
}
